import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { ArrowRight, X } from 'lucide-react'

/**
 * Floating CTA bar — appears after user scrolls past 600px (past hero) on long
 * pillar pages. Reduces conversion friction by reminding visitors of the
 * primary action regardless of scroll depth.
 *
 * Dismiss button stores 24h cookie so users who said "no" don't see it again
 * until tomorrow. SSR-safe: hydrates on client, no scroll listener server-side.
 *
 * Why a single shared component vs per-pillar inline CTA: gives consistent
 * placement + dismissal behavior across all long pages, and one A/B test surface
 * for future copy variations.
 */

interface FloatingCTAProps {
  href?: string
  label?: string
  subtitle?: string
  whatsappMessage?: string
}

const DISMISS_COOKIE = 'floating_cta_dismissed'

export function FloatingCTA({
  href = '/devis-gratuit',
  label = 'Recevez 3 devis gratuits',
  subtitle = "Artisans vérifiés · sous 48h",
  whatsappMessage = "Bonjour, je souhaite obtenir 3 devis.",
}: FloatingCTAProps) {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (document.cookie.includes(`${DISMISS_COOKIE}=1`)) {
      setDismissed(true)
      return
    }
    const onScroll = () => {
      setVisible(window.scrollY > 600)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleDismiss = () => {
    setDismissed(true)
    document.cookie = `${DISMISS_COOKIE}=1; max-age=86400; path=/`
  }

  if (dismissed || !visible) return null

  return (
    <div className="fixed bottom-36 left-4 right-4 z-30 sm:bottom-4 sm:left-auto sm:max-w-sm">
      <div className="relative flex items-center gap-3 rounded-2xl border border-primary/20 bg-white p-3 shadow-2xl ring-1 ring-black/5 sm:p-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900">
            <span className="sm:hidden">Devis gratuit · 48h</span>
            <span className="hidden sm:inline">{label}</span>
          </p>
          <p className="mt-0.5 hidden text-xs text-slate-500 sm:block">{subtitle}</p>
        </div>
        <Link
          to={href}
          aria-label="Demander 3 devis gratuits"
          className="inline-flex min-h-12 items-center gap-1 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
        >
          Devis <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Fermer la bannière de devis"
          className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-500 shadow-md ring-1 ring-slate-200 hover:bg-slate-100"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
